<template lang="pug">
div
  .row
    ul#my-tab.nav.nav-tabs(role='tablist')
      li.nav-item(
        :key='tab.id'
        role='presentation'
        v-for='tab in tabs'
      )
        button.nav-link(
          :aria-controls='tab.id'
          :aria-selected='activeTab === tab.id'
          :class='{ active: activeTab === tab.id }'
          :data-bs-target='`#${tab.id}`'
          :id='`${tab.id}-tab`'
          @click='setTab(tab.id)'
          data-bs-toggle='tab'
          role='tab'
          type='button'
        ) {{ tab.label }}
    #myTabContent.tab-content
      #plan-details.tab-pane.fade(
        :class='{ "show active": activeTab === "plan-details" }'
        aria-labelledby='plan-details'
        role='tabpanel'
        v-if='!isPostpaid'
      )
        PlanDetails(
          :creditCard='creditCard'
          :plan='currentPlan'
          :planStatus='planStatus'
        )
      #plans.tab-pane.fade(
        :class='{ "show active": activeTab === "plans" }'
        aria-labelledby='plans'
        role='tabpanel'
        v-if='!isPostpaid'
      )
        MyPlans(
          :lackPlan='lackPlan'
          :plans='plans'
          @togglePlan='changePlanValues'
        )
      #plans-consumptions.tab-pane.fade(
        :class='{ "show active": activeTab === "plans-consumptions" }'
        aria-labelledby='plans-consumptions'
        role='tabpanel'
      )
        PlanConsumption(
          :isPostpaid='isPostpaid'
          :plan='currentPlan'
          :planStatus='planStatus'
        )
</template>

<script>
import PlanDetails from './PlanDetails.vue';
import MyPlans from './MyPlans.vue';
import PlanConsumption from './PlanConsumption.vue';
import BillingHttp from '@/services/BillingHttp';
import PlanIcon from '@/enums/PlanIcon';

export default {
  name: 'PlanPage',
  components: { PlanDetails, MyPlans, PlanConsumption },
  data() {
    return {
      plans: [
        {
          tag: {
            name: this.$t('pages.plan.customized'),
            icon: 'up-right-and-down-left-from-center',
          },
          title: {
            name: this.$t('pages.plan.corporate'),
            icon: 'rocket',
          },
          label: 'corporate',
          price: null,
          description: this.$t('pages.plan.corporateDescription'),
          benefits: [
            this.$t('pages.planComparison.corporate.benefits.benefit1'),
            this.$t('pages.planComparison.corporate.benefits.benefit2'),
            this.$t('pages.planComparison.corporate.benefits.benefit3'),
            this.$t('pages.planComparison.corporate.benefits.benefit4'),
            this.$t('pages.planComparison.corporate.benefits.benefit5'),
            this.$t('pages.planComparison.corporate.benefits.benefit6'),
          ],
        },
      ],
      lackPlan: '12',
      creditCard: {},
    };
  },
  async created() {
    this.$loading(true);
    try {
      await this.initializePage();
      await this.loadData();
      this.setInitialTab();
    } finally {
      this.equalizeCardDescription();
      this.$loading(false);
    }
  },
  mounted() {
    this.equalizeCardDescription();
    window.addEventListener('resize', this.equalizeCardDescription);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.equalizeCardDescription);
  },
  watch: {
    '$route.query.tab': 'setInitialTab',
  },
  beforeRouteUpdate(to, from, next) {
    this.initializePage();
    next();
  },
  computed: {
    currentPlan() {
      return this.$store.state.subscription.plan.plan;
    },
    isPostpaid() {
      return this.$store.getters['subscription/isPostpaid'];
    },
    planStatus() {
      const statusMap = {
        cancelled: {
          name: this.$t('pages.plan.cancelledPlan'),
          icon: 'ban',
          class: 'alert-simple-danger',
          type: 'cancelled',
        },
        recurring: {
          name: this.$t('pages.plan.activePlan'),
          icon: 'circle-check',
          class: 'alert-simple-success',
          type: 'recurring',
        },
        uninitialized: {
          name: this.$t('pages.plan.awaitingPayment'),
          icon: null,
          class: 'alert-simple-warning',
          type: 'uninitialized',
        },
        active: {
          name: this.$t('pages.plan.activePlan'),
          icon: 'circle-check',
          class: 'alert-simple-success',
          type: 'active',
        },
      };
      return statusMap[this.$store.state.subscription.plan?.state] || {};
    },
    activeTab: {
      get() {
        if (this.isPostpaid) {
          return 'plans-consumptions';
        }
        return this.$route.query.tab || this.defaultTab;
      },
      set(tabId) {
        this.$router.push({ query: { ...this.$route.query, tab: tabId } });
      },
    },
    tabs() {
      if (this.isPostpaid) {
        return [
          {
            id: 'plans-consumptions',
            label: this.$t('pages.plan.consumption'),
          },
        ];
      }

      return [
        { id: 'plan-details', label: this.$t('pages.plan.planDetails') },
        { id: 'plans', label: this.$t('pages.plan.plans') },
        {
          id: 'plans-consumptions',
          label: this.$t('pages.plan.consumption'),
        },
      ];
    },
    defaultTab() {
      return this.isPostpaid ? 'plans-consumptions' : 'plan-details';
    },
  },
  methods: {
    async initializePage() {
      this.$store.dispatch('page/setTitle', this.$t('pages.plan.title'));
      this.$store.dispatch('page/setHead', this.$t('pages.plan.head'));
      this.$store.dispatch('page/setClass', 'account plans');
    },
    async loadData() {
      if (this.isPostpaid) {
        return;
      }
      BillingHttp.supressDefault = true;
      const [plansResult, creditCardResult] = await Promise.allSettled([
        BillingHttp.getPlansList(),
        this.$root.isUSAVersion
          ? Promise.resolve({})
          : BillingHttp.subscriptionsCreditCard(),
      ]);
      this.plans =
        plansResult.status === 'fulfilled'
          ? this.formatPlans(plansResult.value.data.data)
          : null;
      this.creditCard =
        creditCardResult.status === 'fulfilled'
          ? creditCardResult.value.data
          : null;
      this.equalizeCardDescription();
    },
    filterAndSortPlans(plans) {
      const planTypes = this.getPlanType();
      return plans
        .filter((item) => planTypes.includes(item.title.name))
        .sort(
          (a, b) =>
            planTypes.indexOf(a.title.name) - planTypes.indexOf(b.title.name) ||
            b.interval - a.interval
        );
    },
    formatPlans(plansData) {
      for (const plan of plansData.reverse()) {
        const dataPlan = {
          id: plan.id,
          interval: plan.interval,
          tag: {
            name: this.getPlanTag(plan.name),
            icon: this.getPlanTag(plan.name, 'icon'),
          },
          title: {
            name: this.getPlanName(plan.name),
            icon: PlanIcon.getByName(plan.name),
          },
          label: plan.name.toLowerCase(),
          price: this.formatPriceDetails(plan.value_cents),
          currency_symbol: plan.currency_symbol,
          description: plan.description,
          benefits: this.formatedResources(
            plan.resources,
            plan.limits,
            plan.features,
            plan.interval
          ),
        };
        this.plans.push(dataPlan);
      }
      return this.filterAndSortPlans(this.plans);
    },
    getPlanType() {
      return [
        this.$t('pages.plan.basic'),
        this.$t('pages.plan.essential'),
        this.$t('pages.plan.advanced'),
        this.$t('pages.plan.corporate'),
      ];
    },
    getPlanName(planName) {
      const planNames = {
        ['basic']: this.$t('pages.plan.basic'),
        ['essential']: this.$t('pages.plan.essential'),
        ['advanced']: this.$t('pages.plan.advanced'),
        ['corporate']: this.$t('pages.plan.corporate'),
        ['free']: this.$t('pages.planDetails.planNames.freePlan'),
      };
      planName = planName.toLowerCase();
      return planNames[planName] || planName;
    },
    getPlanTag(planName, option = null) {
      const tagPlans = {
        ['basic']: {
          tag: this.$t('pages.plan.basic'),
          icon: 'circle-check',
        },
        ['essential']: {
          tag: this.$t('pages.plan.popular'),
          icon: 'heart',
        },
        ['advanced']: {
          tag: this.$t('pages.plan.mostComplete'),
          icon: 'crown',
        },
      };
      const plan = tagPlans[planName.toLowerCase()];
      if (!plan) {
        //TODO - icon null generated a fonts-awesome icon error
        return option === 'icon' ? null : this.$t('pages.plan.basic');
      }
      switch (option) {
        case 'icon':
          return plan.icon ?? 'circle-check';
        default:
          return plan.tag ?? planName;
      }
    },
    formatPriceDetails(valueCents) {
      const [reais, centavos] = this.formatPrice(valueCents);
      const [monthlyReais, monthlyCentavos] = this.formatPrice(
        this.getMonthlyValue(valueCents)
      );
      return {
        monthly: {
          number: reais,
          cents: `,${centavos}/${this.$t('pages.planDetails.planInterval.1')}`,
        },
        annual: {
          monthly: {
            number: monthlyReais,
            cents: `,${monthlyCentavos}/${this.$t(
              'pages.planDetails.planInterval.1'
            )}`,
          },
          annual: {
            number: reais,
            cents: `,${centavos}/${this.$t(
              'pages.planDetails.planInterval.12'
            )}`,
          },
        },
      };
    },
    formatPrice(price) {
      return (price / 100).toFixed(2).split('.');
    },
    getMonthlyValue(price) {
      return price / 12;
    },
    setTab(tabId) {
      if (this.activeTab === tabId) {
        return;
      }
      this.activeTab = tabId;
      this.$nextTick(() => {
        this.equalizeCardDescription();
      });
    },
    async setInitialTab() {
      const initialTab = this.isPostpaid
        ? this.defaultTab
        : this.$route.query.tab;
      if (this.activeTab === initialTab) {
        return;
      }
      if (initialTab && this.tabs.some((tab) => tab.id === initialTab)) {
        this.activeTab = initialTab;
        await this.initializePage();
      }
    },
    changePlanValues() {
      this.equalizeCardDescription();
      this.lackPlan = this.lackPlan === '12' ? '1' : '12';
    },
    formatedResources(
      resourcesArray = [],
      limitsArray = [],
      featuresArray = [],
      planInterval
    ) {
      const planPeriod =
        planInterval == '12'
          ? this.$t('pages.plan.year')
          : this.$t('pages.plan.month');
      const arrayResources = [];
      resourcesArray.forEach((item) => {
        if (item.resource === 'envelopes') {
          arrayResources.push(
            `${item.quantity} ${this.$t('pages.plan.sendings')} ${this.$t(
              'pages.plan.per'
            )} ${planPeriod}`
          );
        }
      });
      limitsArray.forEach((item) => {
        if (item.limitation === 'storage') {
          arrayResources.push(this.formatStorage(item.quantity));
        } else if (item.limitation === 'users') {
          arrayResources.push(this.formatUsers(item.quantity));
        }
      });
      featuresArray.forEach((item) => {
        if (item.feature === 'whatsapp') {
          arrayResources.push(this.$t('pages.plan.whatsappNotification'));
        } else if (item.feature === 'integration_api') {
          arrayResources.push(this.$t('pages.plan.apiIntegration'));
        }
      });
      return arrayResources;
    },
    formatStorage(quantity) {
      return quantity >= 1000
        ? `${quantity / 1000} ${this.$t('pages.plan.gbStorage')}`
        : `${quantity} ${this.$t('pages.plan.mbStorage')}`;
    },
    formatUsers(quantity) {
      return `${this.$t('pages.plan.upTo')} ${quantity} ${this.$t(
        'pages.plan.users'
      )}`;
    },
    equalizeCardDescription(retries = 1, interval = 100) {
      setTimeout(() => {
        const attemptEqualization = () => {
          const cardsDescription = Array.from(
            this.$el.querySelectorAll('.card-description')
          ).slice(0, 3);
          const maxHeight = Math.max(
            ...cardsDescription.map((el) => el.offsetHeight)
          );
          if (maxHeight > 0 && this.plans.length > 1) {
            cardsDescription.forEach((el) => {
              el.style.height = `${maxHeight}px`;
            });
          } else if (retries > 0) {
            setTimeout(() => attemptEqualization(), interval);
          }
        };
        attemptEqualization();
      }, '1');
    },
  },
};
</script>
